/**
 * Step 1 — Get started.
 *
 * Per `rcp-apply-now-step1-get-started.html`. Product pick cards are
 * radiogroup-buttons (WCAG: interactive elements must have the right role).
 *
 * A11y fix: product cards are `role="radio"` + `aria-checked` (NOT `<div>`).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useApplyWizard } from './ApplyWizard';
import { useToast } from '../../components/a11y/AccessibleToast';
import { getApiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowRight, PiggyBank, Wallet, GraduationCap, Briefcase, type LucideIcon } from 'lucide-react';

interface ProductCard {
  code: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

const PRODUCTS: ReadonlyArray<ProductCard> = [
  { code: 'FC',  title: 'Free Checking',     description: 'No monthly fee. No minimum balance. The simple everyday checking account.', icon: Wallet },
  { code: 'SV',  title: 'Statement Savings', description: 'Earn a little extra on your savings with easy access when you need it.',       icon: PiggyBank },
  { code: 'SCK', title: 'Student Checking',  description: 'Built for students — no-fee checking with a debit card.',                      icon: GraduationCap },
  { code: 'BC',  title: 'Business Checking', description: 'Simple business banking for small businesses and sole proprietors.',          icon: Briefcase },
];

export function Step1GetStarted(): JSX.Element {
  const { setDraft, setProductCode } = useApplyWizard();
  const { identity } = useAuth();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function continueNext(): Promise<void> {
    if (!selectedCode) {
      pushToast('error', 'Please choose an account type', 'Select one of the options above to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const email = identity?.email ?? '';
      const client = getApiClient();
      // TODO(PLANNED /portal/apply/start): server creates draft + cart.
      const res = await client.portal.apply.start({
        email,
        origin: 'apply-now',
        requestedProductCodes: [selectedCode],
      });
      if (res.success && res.data) {
        setDraft(res.data.draftId, res.data.cartId);
        setProductCode(selectedCode);
        navigate('/apply/applicant');
      } else {
        pushToast('error', 'We could not start your application', res.error ?? 'Please try again.');
      }
    } catch {
      pushToast('error', 'Network error', 'Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section aria-labelledby="step1-heading">
      <h1 id="step1-heading" tabIndex={-1}>Let&apos;s get started</h1>
      <p className="lead" style={{ marginBottom: 24 }}>
        Which type of account would you like to open today?
      </p>
      <div
        role="radiogroup"
        aria-labelledby="step1-heading"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}
      >
        {PRODUCTS.map((p) => {
          const checked = selectedCode === p.code;
          const Icon = p.icon;
          return (
            <button
              type="button"
              key={p.code}
              role="radio"
              aria-checked={checked}
              onClick={() => setSelectedCode(p.code)}
              className="card"
              style={{
                textAlign: 'left',
                borderColor: checked ? 'var(--pcb-blue)' : 'var(--surface-border)',
                borderWidth: checked ? 2 : 1,
                boxShadow: checked ? 'var(--sh-3)' : 'var(--sh-1)',
                cursor: 'pointer',
              }}
            >
              <Icon aria-hidden={true} size={28} />
              <h2 style={{ margin: '12px 0 6px 0' }}>{p.title}</h2>
              <p style={{ marginBottom: 0, color: 'var(--ink-700)' }}>{p.description}</p>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void continueNext()}
          disabled={submitting || !selectedCode}
          aria-busy={submitting || undefined}
        >
          {submitting ? 'Working...' : 'Continue'}
          <ArrowRight aria-hidden="true" size={16} />
        </button>
      </div>
    </section>
  );
}
